
import { useState, useEffect, Key, useMemo, ChangeEvent } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Combobox } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import AdminLayout from './AdminLayout';
import apiManager from '../../../lib/api';
import { Category, UserRole, ProductSpecification } from '../../../lib/api/models';
import { createEmptyProductPrices, normalizeProductPrices } from '../../../lib/utils/tarifas';
import ProductVariantsForm from '../components/ProductVariantsForm';

const ProductFormPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<any>({
    name: '',
    description: '',
    promotionMessage: '',
    prices: createEmptyProductPrices(),
    stock: 0,
    isActive: true,
    featured: false,
    searchPriority: 0,
    sku: '',
    brand: '',
    origin: '',
    weight: 0,
    visibleTo: ['customer', 'commercial', 'distributor', 'shop'],
    tags: [],
    variants: [],
    specifications: []
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [query, setQuery] = useState('');


  // Memoize sorted categories with full path
  const sortedCategories = useMemo(() => {
    const getPath = (cat: Category, all: Category[]): string => {
      const parentId = typeof cat.parent === 'string' ? cat.parent : cat.parent?.id;
      if (!parentId) return cat.name;
      const parent = all.find(c => c.id === parentId);
      return parent ? `${getPath(parent, all)} > ${cat.name} ` : cat.name;
    };

    return categories.map(cat => ({
      ...cat,
      displayName: getPath(cat, categories)
    })).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [categories]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Cargar marcas
        try {
          const brandsRes = await apiManager.products.getBrands();
          if (brandsRes.success && brandsRes.data) {
            setBrands(brandsRes.data);
          }
        } catch (error) {
          console.error('Error loading brands:', error);
        }



        // Cargar categorías
        const categoriesRes = await apiManager.categories.getCategories();
        if (categoriesRes.success && categoriesRes.data) {
          setCategories(categoriesRes.data);
        }

        // Si es edición, cargar producto
        if (isEditMode && id) {
          const productRes = await apiManager.products.getProduct(id);
          if (productRes.success && productRes.data) {
            const product = productRes.data;
            console.log('Product loaded:', product);
            console.log('Product category:', product.category);

            if (categoriesRes.success && categoriesRes.data) {
              console.log('Categories loaded (first 2):', categoriesRes.data.slice(0, 2));
              const foundCat = categoriesRes.data.find((c: any) => c.id === (product.category as any)?.id || c._id === (product.category as any)?._id);
              console.log('Matching category in list:', foundCat);
            }

            // Normalizar categoría: si viene como string (ID), buscar el objeto completo
            let categoryData: Category | undefined = undefined;
            if (typeof product.category === 'string' && categoriesRes.success && categoriesRes.data) {
              categoryData = categoriesRes.data.find((c: any) => c.id === product.category || c._id === product.category);
            } else if (product.category && typeof product.category === 'object' && categoriesRes.success && categoriesRes.data) {
              // Si es objeto, asegurar que tenemos la referencia correcta de la lista de categorías (para que coincida el ID)
              const catId = (product.category as any).id || (product.category as any)._id;
              const found = categoriesRes.data.find((c: any) => c.id === catId || c._id === catId);
              if (found) categoryData = found;
            }

            // Crear categoría por defecto si no se encontró ninguna
            const defaultCategory: Category = categoriesRes.data?.[0] || {
              id: '',
              name: '',
              slug: '',
              isActive: true
            };

            setFormData({
              ...product,
              // Asegurar que category sea el objeto completo, nunca null
              category: categoryData || defaultCategory,
              // Asegurar que prices tenga estructura correcta
              // Priorizar nuevo sistema (prices.tarifa1/7/10/12/25) sobre sistema legacy
              prices: product.prices ? normalizeProductPrices(product.prices, product.price || 0) : product.specialPrices ? normalizeProductPrices({
                // Fallback a sistema legacy solo si prices no existe
                tarifa1: product.price || 0,
                tarifa7: product.specialPrices.distributor || product.price || 0,
                tarifa10: product.specialPrices.distributor || product.price || 0,
                tarifa12: product.specialPrices.shop || product.price || 0,
                tarifa25: product.specialPrices.commercial || product.price || 0
              }, product.price || 0) : createEmptyProductPrices(product.price || 0)
            });
          } else {
            navigate('/admin/products');
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id, isEditMode, navigate]);

  const filteredBrands =
    query === ''
      ? brands
      : brands.filter((brand: string) =>
        brand.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase().replace(/\s+/g, ''))
      );



  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });

    // Limpiar error si existe
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };



  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    console.log('🔄 handleCategoryChange - categoryId seleccionado:', categoryId);
    const category = categories.find(cat => (cat as any).id === categoryId || (cat as any)._id === categoryId);
    console.log('🔄 handleCategoryChange - categoría encontrada:', category);

    setFormData({
      ...formData,
      category: category as any
    });
  };

  const handleTagsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Convertir string de tags a array
    const tagsString = e.target.value;
    const tagsArray = tagsString.split(',').map(tag => tag.trim());

    setFormData({
      ...formData,
      tags: tagsArray
    });
  };

  const handleVisibleToChange = (role: UserRole, checked: boolean) => {
    const currentVisibleTo = formData.visibleTo || [];
    let newVisibleTo;

    if (checked) {
      newVisibleTo = [...currentVisibleTo, role];
    } else {
      newVisibleTo = currentVisibleTo.filter((r: UserRole) => r !== role);
    }

    setFormData({
      ...formData,
      visibleTo: newVisibleTo
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      setFiles([...files, ...newFiles]);

      const newUrls = newFiles.map(file => URL.createObjectURL(file));
      setPreviewUrls([...previewUrls, ...newUrls]);
    }
  };

  const removeImagePreview = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);

    const newUrls = [...previewUrls];
    URL.revokeObjectURL(newUrls[index]);
    newUrls.splice(index, 1);
    setPreviewUrls(newUrls);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) newErrors.name = 'El nombre es obligatorio';
    if (!formData.description?.trim()) newErrors.description = 'La descripción es obligatoria';
    if (!formData.category) newErrors.category = 'Debes seleccionar una categoría';

    // Si hay variantes, el precio viene de la primera variante
    // Si no hay variantes, se requiere al menos una variante
    if (formData.variants?.length > 0) {
      const firstVariant = formData.variants[0];
      const price = firstVariant.prices?.tarifa1 || firstVariant.price;
      if (price === undefined || price === null || price <= 0) {
        newErrors.variants = 'La primera variante debe tener un precio (Tarifa 1) mayor a 0';
      }
    } else {
      newErrors.variants = 'Debes añadir al menos una variante con precio';
    }

    if (!formData.sku?.trim()) newErrors.sku = 'El SKU es obligatorio';

    console.log('🔍 Validación - errores encontrados:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('📤 handleSubmit iniciado');
    console.log('📤 formData actual:', formData);

    if (!validateForm()) {
      console.log('❌ Validación fallida');
      return;
    }
    console.log('✅ Validación pasada');

    setIsSaving(true);

    try {
      // Log para debug de categoría
      console.log('🔍 Categoría en formData:', formData.category);
      console.log('🔍 ID de categoría a enviar:', formData.category?.id || formData.category?._id);

      // Preparar datos como JSON (no FormData) para que el backend pueda parsearlo
      // IMPORTANTE: El modelo usa prices.tarifa1/7/10/12/25, no el sistema legacy price + specialPrices
      const productData = {
        name: formData.name,
        description: formData.description,
        promotionMessage: formData.promotionMessage?.trim() || '',
        // Nuevo sistema de precios por tarifas (el modelo espera este formato)
        prices: normalizeProductPrices(formData.prices, formData.price || 0),
        stock: formData.stock || 0,
        category: formData.category?.id || formData.category?._id || '',
        isActive: formData.isActive ?? true,
        featured: formData.featured ?? false,
        searchPriority: Math.max(0, Number(formData.searchPriority) || 0),
        sku: formData.sku || '',
        brand: formData.brand || '',
        origin: formData.origin || '',
        weight: formData.weight || 0,
        visibleTo: formData.visibleTo || [],
        tags: formData.tags || [],
        specifications: formData.specifications || [],
        variants: formData.variants || [],
        ingredients: formData.ingredients || '',
        usageInstructions: formData.usageInstructions || '',
        guaranteedAnalysis: formData.guaranteedAnalysis || {},
        images: formData.images || []
      };

      console.log('📤 Datos a enviar:', productData);

      let response;
      if (isEditMode && id) {
        response = await apiManager.products.updateProduct(id, productData as any);

        // Si hay archivos nuevos de producto, subirlos
        if (files.length > 0) {
          for (const file of files) {
            await apiManager.products.uploadProductImage(id, file);
          }
        }

        // Subir imágenes de variantes
        for (let i = 0; i < formData.variants.length; i++) {
          const variant = formData.variants[i] as any;
          if (variant._newImageFiles && variant._newImageFiles.length > 0) {
            for (const file of variant._newImageFiles) {
              await apiManager.products.uploadVariantImage(id, i, file);
            }
          }
        }
      } else {
        response = await apiManager.products.createProduct(productData as any);

        // Si se creó correctamente y hay archivos, subirlos
        if (response.success && response.data?.id) {
          // Subir imágenes del producto
          if (files.length > 0) {
            for (const file of files) {
              await apiManager.products.uploadProductImage(response.data.id, file);
            }
          }

          // Subir imágenes de variantes
          for (let i = 0; i < formData.variants.length; i++) {
            const variant = formData.variants[i] as any;
            if (variant._newImageFiles && variant._newImageFiles.length > 0) {
              for (const file of variant._newImageFiles) {
                await apiManager.products.uploadVariantImage(response.data.id, i, file);
              }
            }
          }
        }
      }

      if (response.success) {
        navigate('/admin/products');
      } else {
        setErrors({ submit: response.error?.message || 'Error al guardar el producto' });
      }
    } catch (error) {
      console.error('Error saving product:', error);
      setErrors({ submit: 'Error desconocido al guardar el producto' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title={isEditMode ? 'Editar Producto' : 'Nuevo Producto'}>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEditMode ? 'Editar Producto' : 'Nuevo Producto'}>
      {/* Mostrar errores de validación */}
      {Object.keys(errors).length > 0 && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
          <strong className="font-bold">Error de validación:</strong>
          <ul className="mt-2 list-disc list-inside">
            {errors.name && <li>{errors.name}</li>}
            {errors.description && <li>{errors.description}</li>}
            {errors.category && <li>{errors.category}</li>}
            {errors.tarifa1 && <li>{errors.tarifa1}</li>}
            {errors.sku && <li>{errors.sku}</li>}
            {errors.submit && <li>{errors.submit}</li>}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Información básica */}
          <div className="md:col-span-2 space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Información Básica</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del producto <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                </div>

                <div className="relative">
                  <Combobox
                    value={formData.brand || ''}
                    onChange={(value: string) => setFormData({ ...formData, brand: value })}
                  >
                    <Combobox.Label className="block text-sm font-medium text-gray-700 mb-1">
                      Marca
                    </Combobox.Label>
                    <div className="relative mt-1">
                      <Combobox.Input
                        className="w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 md:text-sm text-base"
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                          setQuery(event.target.value);
                          setFormData({ ...formData, brand: event.target.value });
                        }}
                        displayValue={(brand: string) => brand}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center px-2 focus:outline-none">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>

                      {filteredBrands.length > 0 && (
                        <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {filteredBrands.map((brand: string) => (
                            <Combobox.Option
                              key={brand}
                              value={brand}
                              className={({ active }: { active: boolean }) =>
                                `relative cursor-default select-none py-2 pl-8 pr-4 ${active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                                }`
                              }
                            >
                              {({ active, selected }: { active: boolean; selected: boolean }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                    {brand}
                                  </span>
                                  {selected && (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-1.5 ${active ? 'text-white' : 'text-indigo-600'
                                        }`}
                                    >
                                      <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          ))}
                        </Combobox.Options>
                      )}
                    </div>
                  </Combobox>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    SKU <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${errors.sku ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  />
                  {errors.sku && <p className="mt-1 text-sm text-red-500">{errors.sku}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descripción <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    value={formData.description}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  ></textarea>
                  {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Promoción destacada
                  </label>
                  <textarea
                    name="promotionMessage"
                    rows={2}
                    maxLength={300}
                    value={formData.promotionMessage || ''}
                    onChange={handleChange}
                    placeholder="Ej.: 20% de descuento por la compra de más de 10 cajas"
                    className="w-full px-3 py-2 border border-amber-300 rounded-md bg-amber-50 focus:outline-none focus:ring-1 focus:ring-amber-500"
                  />
                  <div className="mt-1 flex justify-between gap-4 text-xs text-gray-500">
                    <span>Mensaje informativo. El descuento se aplicará posteriormente en contabilidad.</span>
                    <span>{(formData.promotionMessage || '').length}/300</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Categoría <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={(formData.category as any)?.id || (formData.category as any)?._id || ''}
                    onChange={handleCategoryChange}
                    className={`w-full px-3 py-2 border ${errors.category ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500`}
                  >
                    <option value="">Selecciona una categoría</option>
                    {sortedCategories.map(category => (
                      <option key={(category as any).id || (category as any)._id} value={(category as any).id || (category as any)._id}>
                        {category.displayName}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-sm text-red-500">{errors.category}</p>}

                  {/* Botón de depuración para recargar categorías */}
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        console.log('Recargando categorías manualmente...');
                        const response = await apiManager.categories.getCategories();
                        console.log('Respuesta manual de categorías:', response);

                        if (response.success && Array.isArray(response.data)) {
                          console.log(`Encontradas ${response.data.length} categorías manualmente`);
                          setCategories(response.data);
                        } else {
                          console.error('Formato de respuesta de categorías inesperado (manual):', response);
                        }
                      } catch (error) {
                        console.error('Error al cargar categorías manualmente:', error);
                      }
                    }}
                    className="mt-2 px-3 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded-md transition-colors"
                  >
                    Recargar categorías
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marca
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Origen
                  </label>
                  <input
                    type="text"
                    name="origin"
                    value={formData.origin || ''}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Etiquetas (separadas por comas)
                  </label>
                  <input
                    type="text"
                    name="tags"
                    value={formData.tags?.join(', ') || ''}
                    onChange={handleTagsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {/* Información Nutricional para Alimentación Animal */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Información Nutricional (Alimentación Animal)</h2>

              {/* Ingredientes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ingredientes
                </label>
                <textarea
                  name="ingredients"
                  rows={3}
                  value={formData.ingredients || ''}
                  onChange={handleChange}
                  placeholder="Lista de ingredientes en texto corrido..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <p className="mt-1 text-xs text-gray-500">Ejemplo: Carne de pollo, arroz integral, grasa de pollo, proteína de huevo...</p>
              </div>

              {/* Análisis Garantizado */}
              <div className="mb-6">
                <h3 className="text-md font-medium text-gray-800 mb-3">Análisis Garantizado</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Proteína Cruda
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.crudeProtein || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          crudeProtein: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 30%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grasa Cruda
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.crudeFat || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          crudeFat: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 15%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fibra Cruda
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.crudeFiber || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          crudeFiber: e.target.value
                        }
                      })}
                      placeholder="Ej: Máx. 5%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Humedad
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.moisture || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          moisture: e.target.value
                        }
                      })}
                      placeholder="Ej: Máx. 12%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Cenizas
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.ash || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          ash: e.target.value
                        }
                      })}
                      placeholder="Ej: Máx. 8%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Calcio
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.calcium || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          calcium: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 1.2%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fósforo
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.phosphorus || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          phosphorus: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 0.9%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Omega-3
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.omega3 || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          omega3: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 0.5%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Omega-6
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.omega6 || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          omega6: e.target.value
                        }
                      })}
                      placeholder="Ej: Mín. 3%"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vitaminas
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.vitamins || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          vitamins: e.target.value
                        }
                      })}
                      placeholder="Ej: Vitaminas A, D3, E"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Minerales
                    </label>
                    <input
                      type="text"
                      value={formData.guaranteedAnalysis?.minerals || ''}
                      onChange={(e) => setFormData({
                        ...formData,
                        guaranteedAnalysis: {
                          ...formData.guaranteedAnalysis!,
                          minerals: e.target.value
                        }
                      })}
                      placeholder="Ej: Zinc, Hierro, Manganeso, Cobre"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>

              {/* Modo de Empleo */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Modo de Empleo / Instrucciones de Uso
                </label>
                <textarea
                  name="usageInstructions"
                  rows={4}
                  value={formData.usageInstructions || ''}
                  onChange={handleChange}
                  placeholder="Instrucciones de cómo usar el producto, dosificación, etc..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
              </div>

              {/* Especificaciones */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Especificaciones Técnicas
                </label>
                {formData.specifications && formData.specifications.length > 0 ? (
                  <div className="space-y-2 mb-3">
                    {formData.specifications.map((spec: ProductSpecification, index: number) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={spec.name}
                          onChange={(e) => {
                            const newSpecs = [...(formData.specifications || [])];
                            newSpecs[index] = { ...spec, name: e.target.value };
                            setFormData({ ...formData, specifications: newSpecs });
                          }}
                          placeholder="Nombre (ej: Edad recomendada)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <input
                          type="text"
                          value={spec.value}
                          onChange={(e) => {
                            const newSpecs = [...(formData.specifications || [])];
                            newSpecs[index] = { ...spec, value: e.target.value };
                            setFormData({ ...formData, specifications: newSpecs });
                          }}
                          placeholder="Valor (ej: Adultos)"
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newSpecs = formData.specifications?.filter((_: ProductSpecification, i: number) => i !== index);
                            setFormData({ ...formData, specifications: newSpecs });
                          }}
                          className="px-3 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setFormData({
                      ...formData,
                      specifications: [...(formData.specifications || []), { name: '', value: '' }]
                    });
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                >
                  + Agregar Especificación
                </button>
              </div>
            </div>

            {/* Nota: Los precios, stock y peso se manejan desde las variantes */}

            {/* Opciones */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Opciones</h2>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => handleChange({
                      ...e,
                      target: {
                        ...e.target,
                        name: 'isActive',
                        value: e.target.checked ? 'true' : 'false',
                        type: 'checkbox'
                      }
                    })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="block text-sm font-medium text-gray-700">
                    Producto activo
                  </label>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    name="featured"
                    id="featured"
                    checked={formData.featured}
                    onChange={(e) => handleChange({
                      ...e,
                      target: {
                        ...e.target,
                        name: 'featured',
                        value: e.target.checked ? 'true' : 'false',
                        type: 'checkbox'
                      }
                    })}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                  />
                  <label htmlFor="featured" className="block text-sm font-medium text-gray-700">
                    Producto destacado
                  </label>
                </div>

                <div>
                  <label htmlFor="searchPriority" className="block text-sm font-medium text-gray-700 mb-1">
                    Prioridad en búsqueda (0 = normal)
                  </label>
                  <input
                    type="number"
                    name="searchPriority"
                    id="searchPriority"
                    min="0"
                    step="1"
                    value={formData.searchPriority ?? 0}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="mt-6">
                <h3 className="text-md font-medium text-gray-800 mb-2">Visible para</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(['customer', 'commercial', 'distributor', 'shop'] as UserRole[]).map(role => (
                    <div key={role} className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id={`role - ${role} `}
                        checked={formData.visibleTo?.includes(role) || false}
                        onChange={(e) => handleVisibleToChange(role, e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`role - ${role} `} className="block text-sm font-medium text-gray-700">
                        {role === 'customer' ? 'Clientes' :
                          role === 'commercial' ? 'Comerciales' :
                            role === 'distributor' ? 'Distribuidores' :
                              role === 'shop' ? 'Tiendas' : role}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Imágenes */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Imágenes</h2>

              {/* Imágenes existentes */}
              {formData.images && formData.images.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-md font-medium text-gray-700 mb-2">Imágenes actuales</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.images.map((image: { id: Key | null | undefined; url: string | undefined; alt: any; isFeatured: any; }, index: any) => (
                      <div key={image.id} className="relative rounded-md overflow-hidden border border-gray-200">
                        <img
                          src={image.url}
                          alt={image.alt || 'Imagen de producto'}
                          className="h-32 w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                images: formData.images?.filter((_: any, i: any) => i !== index)
                              });
                            }}
                            className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                        {image.isFeatured && (
                          <div className="absolute top-2 right-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-md">
                            Principal
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Nuevas imágenes */}
              <div>
                <h3 className="text-md font-medium text-gray-700 mb-2">Añadir imágenes</h3>
                <input
                  type="file"
                  onChange={handleImageChange}
                  multiple
                  accept="image/*"
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />

                {/* Previsualización de nuevas imágenes */}
                {previewUrls.length > 0 && (
                  <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                    {previewUrls.map((url, index) => (
                      <div key={index} className="relative rounded-md overflow-hidden border border-gray-200">
                        <img
                          src={url}
                          alt={`Vista previa ${index + 1} `}
                          className="h-32 w-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removeImagePreview(index)}
                            className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Variantes del producto */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <ProductVariantsForm
                variants={formData.variants || []}
                onChange={(variants) => {
                  // Auto-fill main product data from first variant if exists
                  const firstVariant = variants[0];
                  if (firstVariant) {
                    console.log('🔄 Auto-filling from first variant:', firstVariant);
                    setFormData((prev: any) => ({
                      ...prev,
                      variants,
                      // Auto-complete prices from first variant (siempre sobrescribir si la variante tiene valores)
                      prices: normalizeProductPrices({
                        tarifa1: firstVariant.prices?.tarifa1 !== undefined ? firstVariant.prices.tarifa1 : (firstVariant.price || prev.prices?.tarifa1 || 0),
                        tarifa7: firstVariant.prices?.tarifa7 !== undefined ? firstVariant.prices.tarifa7 : (prev.prices?.tarifa7 || 0),
                        tarifa10: firstVariant.prices?.tarifa10 !== undefined ? firstVariant.prices.tarifa10 : (prev.prices?.tarifa10 || 0),
                        tarifa12: firstVariant.prices?.tarifa12 !== undefined ? firstVariant.prices.tarifa12 : (prev.prices?.tarifa12 || 0),
                        tarifa25: firstVariant.prices?.tarifa25 !== undefined ? firstVariant.prices.tarifa25 : (prev.prices?.tarifa25 || 0)
                      }, firstVariant.price || prev.prices?.tarifa1 || 0),
                      // Auto-complete stock and weight from first variant
                      stock: firstVariant.stock !== undefined ? firstVariant.stock : (prev.stock || 0),
                      weight: firstVariant.weight !== undefined ? firstVariant.weight : (prev.weight || 0)
                    }));
                  } else {
                    setFormData((prev: any) => ({ ...prev, variants }));
                  }
                }}
                userRole="admin"
              />
              {formData.variants?.length > 0 && (
                <p className="mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  💡 Los precios, stock y peso del producto se autocompletaron con los datos de la primera variante
                </p>
              )}
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/admin/products')}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSaving}
                className={`px-6 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white ${isSaving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
              >
                {isSaving ? 'Guardando...' : 'Guardar Producto'}
              </button>
            </div>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
};

export default ProductFormPage; 
